import type { Image, Paragraph } from 'mdast';

import { fg } from '@atlaskit/platform-feature-flags';

import type { State } from '../state';

/**
 * When the schema uses `media` / `mediaSingle` instead of `image` (Confluence
 * markdown preset) and the paragraph is a lone image, emit
 * `mediaSingle > media (type: "external")` so `![alt](url)` round-trips
 * instead of degrading to alt-text only. Mixed-content paragraphs fall
 * through to the generic handler.
 */
const tryEmitMediaSingle = (state: State, node: Paragraph): boolean => {
	const { mediaSingle, media } = state.schema.nodes;
	if (!mediaSingle || !media) {
		return false;
	}
	if (!isLoneImageParagraph(node)) {
		return false;
	}
	const image = node.children[0] as Image;
	const url = typeof image.url === 'string' && image.url.length > 0 ? image.url : undefined;
	if (!url) {
		return false;
	}
	state.openNode(mediaSingle);
	state.addNode(media, { url, alt: image.alt ?? null, type: 'external' }, []);
	state.closeNode();
	return true;
};

/** Render a markdown paragraph into the ProseMirror schema. */
export function paragraph(state: State, node: Paragraph): void {
	const { paragraph } = state.schema.nodes;
	if (!paragraph) {
		// Gracefully degrade: schema has no paragraph node — render inline children directly
		state.parseTokens(node.children);
		return;
	}
	// Structural checks first so we only consult the feature gate (and fire
	// its exposure event) on the cases we actually care about.
	if (
		!state.schema.nodes.image &&
		isLoneImageParagraph(node) &&
		fg('platform_editor_md_preset_image_to_media')
	) {
		if (tryEmitMediaSingle(state, node)) {
			return;
		}
	}
	state.openNode(paragraph);
	state.parseTokens(node.children);
	state.closeNode();
}

const isLoneImageParagraph = (node: Paragraph): boolean =>
	node.children.length === 1 && node.children[0].type === 'image';
